/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */
(function () {
  "use strict";

  var container = document.getElementById("mastConfigurator");
  if (!container) return;

  var dataEl = document.getElementById("mcData");
  if (!dataEl) return;
  var config = JSON.parse(dataEl.textContent);
  var variationData = config.variationData;
  var currentHandle = config.currentHandle;
  var currentRange = config.currentRange;
  var currentDiameter = config.currentDiameter;
  var currentCurve = config.currentCurve;
  var currentLength = config.currentLength;

  /* ---------------------------------------------------------------
     SCROLL REVEAL
     --------------------------------------------------------------- */
  var revealObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-revealed");
          revealObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: "0px 0px -60px 0px" },
  );

  container.querySelectorAll(".config-section").forEach(function (section) {
    revealObserver.observe(section);
  });

  /* ---------------------------------------------------------------
     ANIMATED PRICE UPDATE
     --------------------------------------------------------------- */
  function animatePrice(el, newText) {
    if (!el || el.textContent === newText) return;
    el.classList.add("is-updating");
    setTimeout(function () {
      el.textContent = newText;
      el.classList.remove("is-updating");
    }, 150);
  }

  /* ---------------------------------------------------------------
     ACCESSORY DATA (from metaobject, injected via Liquid)
     --------------------------------------------------------------- */
  var accessories = config.accessories || {};
  var currentVariantId = config.currentVariantId;

  /* ---------------------------------------------------------------
     HELPERS
     --------------------------------------------------------------- */
  function createEl(tag, className, text) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (text) el.textContent = text;
    return el;
  }

  /* ---------------------------------------------------------------
     RANGE METADATA
     --------------------------------------------------------------- */
  var rangeData = {
    essentials: { carbon: "50%", fiber: "Torayca T700", weight: "2.69 kg" },
    hd: { carbon: "75%", fiber: "Torayca T700 Pre-preg", weight: "2.10 kg" },
    elite: { carbon: "100%", fiber: "Torayca T800 Pre-preg", weight: "1.45 kg" },
  };
  var curveLabels = { constant: "Constant Curve", fh: "Constant FH Curve", fl: "Constant FL Curve" };

  /* ---------------------------------------------------------------
     NAVIGATION: lookup from mast_selector_group metaobject data
     --------------------------------------------------------------- */
  var mastProducts = config.mastProducts || {};
  var catalogVariants = (variationData && variationData.variants) || [];

  function findMastProduct(range, diameter, curve, length) {
    var key = diameter + "_" + length + "_" + curve;
    var group = mastProducts[key];
    if (!group) return null;
    return group[range] || null;
  }

  function findCatalogVariant(diameter, curve, length) {
    return (
      catalogVariants.find(function (v) {
        return v.diameter === diameter && v.bendcurve === curve && String(v.length) === String(length);
      }) || null
    );
  }

  function resolveMastSelection(range, diameter, curve, length) {
    return (
      findMastProduct(range, diameter, curve, length) ||
      (function () {
        var variant = findCatalogVariant(diameter, curve, length);
        if (!variant) return null;
        return {
          handle: variant.handle,
          sku: variant.sku,
          variantId: null,
          price: null,
          available: variant.available,
        };
      })()
    );
  }

  /*
   * Flipping diameter (RDM<->SDM) often lands on a length that doesn't exist in
   * the target diameter (RDM = 340-460, SDM = 400-490). Find the closest length
   * that DOES exist in that diameter so the switch never dead-ends. Prefer keeping
   * the current curve+range; relax them only if the diameter has no product there
   * at all. Mirrors the cross-diameter length fallback in the click handler.
   * Returns a selection object {handle, range, diameter, curve, length} or null.
   */
  function nearestMastForDiameter(range, diameter, curve, length) {
    var want = parseInt(length, 10);
    var curveOrder = [curve, "constant", "fh", "fl"];
    var rangeOrder = [range, "hd", "essentials", "elite"];
    for (var ci = 0; ci < curveOrder.length; ci++) {
      for (var ri = 0; ri < rangeOrder.length; ri++) {
        var best = null;
        container.querySelectorAll('.config-card[data-step="length"]').forEach(function (card) {
          var len = card.dataset.value;
          var allowed = (card.dataset.diameters || "").split(",").filter(Boolean);
          if (allowed.length && allowed.indexOf(diameter) === -1) return; // not offered in this diameter
          var mast = resolveMastSelection(rangeOrder[ri], diameter, curveOrder[ci], len);
          if (!mast || !mast.handle) return;
          var dist = Math.abs(parseInt(len, 10) - want);
          if (!best || dist < best.dist) best = { dist: dist, handle: mast.handle, length: len };
        });
        if (best) {
          return {
            handle: best.handle,
            range: rangeOrder[ri],
            diameter: diameter,
            curve: curveOrder[ci],
            length: best.length,
          };
        }
      }
    }
    return null;
  }

  function updateSelectedMast() {
    var range = getSelectionOrDefault("range", currentRange);
    var diameter = getSelectionOrDefault("diameter", currentDiameter);
    var curve = getSelectionOrDefault("curve", currentCurve);
    var length = getSelectionOrDefault("length", currentLength);
    var mast = resolveMastSelection(range, diameter, curve, length);
    if (mast && mast.variantId) {
      currentVariantId = mast.variantId;
      var dynamicInput = document.getElementById("mcDynamicVariantInput");
      if (dynamicInput) dynamicInput.value = currentVariantId;
    }
    updateRangePrices();
    updateOptionAvailability();
    updateSummary();
  }

  function updateOptionAvailability() {
    var selDia = getSelectionOrDefault("diameter", currentDiameter);
    var selCurve = getSelectionOrDefault("curve", currentCurve);
    var selLength = getSelectionOrDefault("length", currentLength);
    var selRange = getSelectionOrDefault("range", currentRange);

    container.querySelectorAll('.config-card[data-step="curve"]').forEach(function (card) {
      var curve = card.dataset.value;
      var hasVariant =
        catalogVariants.some(function (v) {
          return v.bendcurve === curve && v.diameter === selDia;
        }) ||
        Object.keys(mastProducts).some(function (key) {
          var parts = key.split("_");
          return parts[0] === selDia && parts[2] === curve;
        });
      card.classList.toggle("is-disabled", !hasVariant);
      card.setAttribute("aria-disabled", hasVariant ? "false" : "true");
    });

    container.querySelectorAll('.config-card[data-step="diameter"]').forEach(function (card) {
      var dia = card.dataset.value;
      /* Enabled if this diameter exists at the current length OR any other length
         (clicking then jumps to the nearest available length). Only fully absent
         diameters stay disabled. */
      var hasVariant =
        !!resolveMastSelection(selRange, dia, selCurve, selLength) ||
        !!nearestMastForDiameter(selRange, dia, selCurve, selLength);
      card.classList.toggle("is-disabled", !hasVariant);
      card.setAttribute("aria-disabled", hasVariant ? "false" : "true");
    });

    container.querySelectorAll('.config-card[data-step="length"]').forEach(function (card) {
      var length = card.dataset.value;
      var allowedDiameters = (card.dataset.diameters || "").split(",").filter(Boolean);
      var diaOk = allowedDiameters.indexOf(selDia) !== -1;
      var variant = findCatalogVariant(selDia, selCurve, length);
      var mast = resolveMastSelection(selRange, selDia, selCurve, length);
      var existsForDia = diaOk && (!!variant || !!mast);
      var existsCrossDia =
        !diaOk &&
        allowedDiameters.some(function (d) {
          return !!findCatalogVariant(d, selCurve, length) || !!resolveMastSelection(selRange, d, selCurve, length);
        });
      var exists = existsForDia || existsCrossDia;

      card.classList.toggle("is-disabled", !exists);
      card.classList.toggle("is-unavailable-for-diameter", !diaOk && existsCrossDia);
      card.classList.toggle("is-out-of-stock", exists && variant && variant.available === false);
      card.setAttribute("aria-disabled", exists ? "false" : "true");

      var right = card.querySelector(".config-card__right");
      var hint = card.querySelector(".config-card__diameter-hint");
      var priceEl = card.querySelector(".config-card__price");

      if (!diaOk) {
        if (priceEl) priceEl.remove();
        if (!hint && right) {
          hint = createEl("div", "config-card__diameter-hint", allowedDiameters.join(" / ") + " only");
          right.appendChild(hint);
        } else if (hint) {
          hint.textContent = allowedDiameters.join(" / ") + " only";
        }
      } else if (hint) {
        hint.remove();
      }

      if (exists && mast && mast.price) {
        if (!right) {
          right = createEl("div", "config-card__right");
          card.appendChild(right);
        }
        if (!priceEl) {
          priceEl = createEl("div", "config-card__price");
          right.appendChild(priceEl);
        }
        priceEl.textContent = formatPrice(mast.price);
      } else if (priceEl) {
        priceEl.remove();
      }
    });
  }

  function formatPrice(cents) {
    if (!cents) return "--";
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency: config.currency || window.Shopify?.currency?.active || "EUR",
      }).format(cents / 100);
    } catch (error) {
      return (config.currency || "EUR") + " " + (cents / 100).toFixed(2).replace(/\.00$/, "");
    }
  }

  function updateRangePrices() {
    var diameter = getSelectionOrDefault("diameter", currentDiameter);
    var curve = getSelectionOrDefault("curve", currentCurve);
    var length = getSelectionOrDefault("length", currentLength);

    var ranges = ["essentials", "hd", "elite"];
    var priceIds = { essentials: "mcPriceEssentials", hd: "mcPriceHd", elite: "mcPriceElite" };

    ranges.forEach(function (r) {
      var mast = findMastProduct(r, diameter, curve, length);
      var el = document.getElementById(priceIds[r]);
      if (el && mast && mast.price) {
        el.textContent = formatPrice(mast.price);
      } else if (el) {
        el.textContent = "--";
      }
      /* Update weight on range card */
      var weightEl = container.querySelector('[data-step="range"][data-value="' + r + '"] .config-card__weight');
      if (weightEl) {
        var weight = mast && mast.weight ? mast.weight : rangeData[r] ? rangeData[r].weight : "--";
        weightEl.textContent = weight;
        weightEl.classList.toggle("has-tolerance", Boolean(weight && weight !== "--"));
      }
    });

    /* Price delta chips: delta vs the currently SELECTED range, not always vs HD.
       Essentials selected -> HD "+$187", Elite "+$287".
       HD selected       -> Essentials "Save $187", Elite "+$100".
       Elite selected    -> Essentials "Save $287", HD "Save $100". */
    var selectedRange = getSelectionOrDefault("range", currentRange);
    var rangePrices = {};
    var deltaIds = { essentials: "mcDeltaEssentials", hd: "mcDeltaHd", elite: "mcDeltaElite" };

    ranges.forEach(function (r) {
      var mast = findMastProduct(r, diameter, curve, length);
      rangePrices[r] = mast && mast.price ? mast.price : 0;
    });

    var basePrice = rangePrices[selectedRange] || 0;
    ranges.forEach(function (r) {
      var deltaEl = document.getElementById(deltaIds[r]);
      if (!deltaEl) return;
      deltaEl.textContent = "";
      deltaEl.className = "range-card__price-delta";
      var price = rangePrices[r];
      if (!price || !basePrice || r === selectedRange) return;
      var diff = price - basePrice;
      if (diff > 0) {
        deltaEl.textContent = "+" + formatPrice(diff);
        deltaEl.classList.add("range-card__price-delta--upgrade");
      } else if (diff < 0) {
        deltaEl.textContent = "Save " + formatPrice(-diff);
        deltaEl.classList.add("range-card__price-delta--save");
      }
    });
  }

  /* ---------------------------------------------------------------
     GET CURRENT SELECTIONS (from UI or defaults)
     --------------------------------------------------------------- */
  function getSelection(step) {
    var card = container.querySelector('.config-card[data-step="' + step + '"].is-selected');
    return card ? card.dataset.value : null;
  }

  function getSelectionOrDefault(step, fallback) {
    return getSelection(step) || fallback;
  }

  /* ---------------------------------------------------------------
     ACCESSORY CARD BUILDER
     --------------------------------------------------------------- */
  function buildAccessoryCard(item, stepName) {
    var card = createEl("div", "config-card config-card--upsell-img");
    card.setAttribute("role", "button");
    card.setAttribute("tabindex", "0");
    card.setAttribute("aria-pressed", "false");
    card.dataset.step = stepName;
    card.dataset.value = item.handle || item.title;
    card.dataset.variantId = item.variantId || "";
    card.dataset.addonPrice = item.price;

    var thumb = createEl("div", "config-card__thumb");
    var thumbImg = document.createElement("img");
    thumbImg.src = item.image || "";
    thumbImg.alt = item.title;
    thumbImg.loading = "lazy";
    thumb.appendChild(thumbImg);
    card.appendChild(thumb);

    var left = createEl("div", "config-card__left");
    left.appendChild(createEl("div", "config-card__title", item.title));
    card.appendChild(left);

    var right = createEl("div", "config-card__right");
    right.appendChild(createEl("div", "config-card__price", formatPrice(item.price)));
    card.appendChild(right);

    card.addEventListener("click", function () {
      var parent = this.parentElement;
      parent.querySelectorAll('.config-card[data-step="' + stepName + '"]').forEach(function (c) {
        setSelected(c, false);
      });
      setSelected(this, true);
      updateSummary();
    });
    card.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.click();
      }
    });

    return card;
  }

  function renderAccessoryCards(containerId, items, stepName, preselectFirst) {
    var el = document.getElementById(containerId);
    if (!el) return;
    while (el.firstChild) el.removeChild(el.firstChild);

    var firstCard = null;
    items.forEach(function (item) {
      if (item.available !== false) {
        var card = buildAccessoryCard(item, stepName);
        if (!firstCard) firstCard = card;
        el.appendChild(card);
      }
    });

    if (preselectFirst && firstCard) {
      setSelected(firstCard, true);
    }

    var noCard = createEl("div", "config-card");
    noCard.setAttribute("role", "button");
    noCard.setAttribute("tabindex", "0");
    noCard.setAttribute("aria-pressed", "false");
    noCard.dataset.step = stepName;
    noCard.dataset.value = "none";
    noCard.dataset.variantId = "";
    noCard
      .appendChild(createEl("div", "config-card__left"))
      .appendChild(
        createEl("div", "config-card__title", "No " + (stepName === "extension" ? "Extension" : "Baseplate")),
      );
    noCard.addEventListener("click", function () {
      el.querySelectorAll('.config-card[data-step="' + stepName + '"]').forEach(function (c) {
        setSelected(c, false);
      });
      setSelected(this, true);
      updateSummary();
    });
    noCard.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.click();
      }
    });
    el.appendChild(noCard);

    /* Default to "None" (opt-in upsell) when not preselecting the first item. */
    if (!preselectFirst) setSelected(noCard, true);
  }

  function getAccessoryGroup(range, diameter) {
    var key = range + "_" + diameter;
    return accessories[key] || accessories["hd_RDM"] || { extensions: [], baseplates: [] };
  }

  function updateAccessories() {
    var selectedRange = getSelectionOrDefault("range", currentRange);
    var selectedDiameter = getSelectionOrDefault("diameter", currentDiameter);
    var group = getAccessoryGroup(selectedRange, selectedDiameter);
    renderAccessoryCards("mcExtensionCards", group.extensions || [], "extension", false);
    renderAccessoryCards("mcBaseplateCards", group.baseplates || [], "baseplate", false);
  }

  /* ---------------------------------------------------------------
     UPDATE SUMMARY / PRICE / COLLAPSE
     --------------------------------------------------------------- */
  function updateSummary() {
    var total = 0;

    var lengthCard = container.querySelector('.config-card[data-step="length"].is-selected');
    var diameterCard = container.querySelector('.config-card[data-step="diameter"].is-selected');
    var rangeCard = container.querySelector('.config-card[data-step="range"].is-selected');
    var curveCard = container.querySelector('.config-card[data-step="curve"].is-selected');
    var extCard = container.querySelector('.config-card[data-step="extension"].is-selected');
    var bpCard = container.querySelector('.config-card[data-step="baseplate"].is-selected');

    /* Base price from mast_selector_group lookup (prices in cents) */
    var selRange = rangeCard ? rangeCard.dataset.value : currentRange;
    var selDia = diameterCard ? diameterCard.dataset.value : currentDiameter;
    var selCurve = curveCard ? curveCard.dataset.value : currentCurve;
    var selLength = lengthCard ? lengthCard.dataset.value : currentLength;
    var selectedMast = resolveMastSelection(selRange, selDia, selCurve, selLength);
    if (selectedMast && selectedMast.price) {
      total = selectedMast.price;
    }

    if (extCard && extCard.dataset.value !== "none") total += parseFloat(extCard.dataset.addonPrice) || 0;
    if (bpCard && bpCard.dataset.value !== "none") total += parseFloat(bpCard.dataset.addonPrice) || 0;
    var totalCents = Math.round(total * 100);

    var priceStr = formatPrice(total);

    /* Recap values */
    var rangeVal = rangeCard ? rangeCard.dataset.value : currentRange;
    var rangeTitleEl = rangeCard
      ? rangeCard.querySelector(".range-card__name") || rangeCard.querySelector(".config-card__title")
      : null;
    var rangeTitle = rangeTitleEl ? rangeTitleEl.textContent : currentRange.toUpperCase();
    var diaVal = diameterCard ? diameterCard.dataset.value : currentDiameter;
    var curveVal = curveCard ? curveCard.dataset.value : currentCurve;
    var lengthVal = lengthCard ? lengthCard.dataset.value : currentLength;
    var rd = rangeData[rangeVal] || rangeData.hd;

    var recapTitle = document.getElementById("mcRecapTitle");
    var recapSubtitle = document.getElementById("mcRecapSubtitle");
    var recapLength = document.getElementById("mcRecapLength");
    var recapDiameter = document.getElementById("mcRecapDiameter");
    var recapCurve = document.getElementById("mcRecapCurve");
    var recapRange = document.getElementById("mcRecapRange");
    var recapExtras = document.getElementById("mcRecapExtras");

    if (recapTitle)
      recapTitle.textContent = rangeTitle + " " + diaVal + " " + (curveLabels[curveVal] || curveVal) + " " + lengthVal;
    if (recapCurve) recapCurve.textContent = curveLabels[curveVal] || curveVal;
    if (recapDiameter) recapDiameter.textContent = diaVal;
    if (recapLength) recapLength.textContent = lengthVal + " cm";
    if (recapRange) recapRange.textContent = rangeTitle;

    var extras = [];
    if (extCard && extCard.dataset.value !== "none") {
      var extTitle = extCard.querySelector(".config-card__title");
      extras.push(extTitle ? extTitle.textContent : "Extension");
    }
    if (bpCard && bpCard.dataset.value !== "none") {
      var bpTitle = bpCard.querySelector(".config-card__title");
      extras.push(bpTitle ? bpTitle.textContent : "Baseplate");
    }
    if (recapExtras) recapExtras.textContent = extras.length ? "+ " + extras.join(" + ") : "";

    /* Sticky title */
    var stickyTitle = document.getElementById("mcStickyTitle");
    if (stickyTitle) stickyTitle.textContent = rangeTitle + " " + diaVal + " " + lengthVal;

    /* Prices */
    animatePrice(document.getElementById("mcBuyPrice"), priceStr);
    animatePrice(document.getElementById("mcStickyPrice"), priceStr);

    /* Free shipping offer */
    if (window.MastFreeShipping) {
      var shippingEl = document.getElementById("mcShippingOffer");
      window.MastFreeShipping.updateOffer(shippingEl, totalCents);
    }

    /* SKU line */
    var skuLine = document.getElementById("mcSkuLine");
    if (skuLine) skuLine.textContent = selectedMast && selectedMast.sku ? "SKU: " + selectedMast.sku : "";

    /* Summary values */
    var summaryLength = document.getElementById("mcSummaryLength");
    var summaryDiameter = document.getElementById("mcSummaryDiameter");
    var summaryRange = document.getElementById("mcSummaryRange");
    var summaryCurve = document.getElementById("mcSummaryCurve");
    if (summaryLength) summaryLength.textContent = lengthVal + " cm";
    if (summaryDiameter) summaryDiameter.textContent = diaVal;
    if (summaryRange && rangeCard) summaryRange.textContent = rangeTitle;
    if (summaryCurve) summaryCurve.textContent = curveLabels[curveVal] || curveVal;

    /* ATC gate: core config only (curve + diameter + length) */
    var requiredSteps = ["curve", "diameter", "length"];
    var missing = [];
    requiredSteps.forEach(function (step) {
      if (!container.querySelector('.config-card[data-step="' + step + '"].is-selected')) {
        missing.push(step);
      }
    });

    var catalogMatch = findCatalogVariant(selDia, selCurve, selLength);
    var isInStock = !catalogMatch || catalogMatch.available !== false;
    var hasVariant = !!(selectedMast && (selectedMast.variantId || currentVariantId));
    var isReady = missing.length === 0 && hasVariant && isInStock;
    var atcBtn = document.getElementById("mcAtcButton");
    var stickyAtcBtn = document.getElementById("mcStickyAtcBtn");
    var atcHint = document.getElementById("mcAtcHint");

    if (atcBtn) {
      var wasDisabled = atcBtn.disabled;
      atcBtn.disabled = !isReady;
      if (isReady && wasDisabled) {
        atcBtn.classList.add("is-ready");
        setTimeout(function () {
          atcBtn.classList.remove("is-ready");
        }, 500);
      }
    }
    if (stickyAtcBtn) stickyAtcBtn.disabled = !isReady;
    var dynamicCheckout = document.querySelector("#mcDynamicCheckoutForm .shopify-payment-button");
    if (dynamicCheckout) dynamicCheckout.style.display = isReady ? "" : "none";
    if (atcHint) {
      if (missing.length) {
        atcHint.textContent = "Select " + missing.join(", ");
      } else if (!isInStock) {
        atcHint.textContent = "This configuration is currently out of stock";
      } else if (!hasVariant) {
        atcHint.textContent = "This combination is not available";
      } else {
        atcHint.textContent = "";
      }
    }

    /* Collapse/dim mast config steps (1-4) only */
    var mastSteps = ["step-curve", "step-diameter", "step-length", "step-range"];
    var mastDataSteps = ["curve", "diameter", "length", "range"];
    var firstIncomplete = -1;

    mastSteps.forEach(function (id, i) {
      var el = document.getElementById(id);
      if (!el) return;
      var hasSelection = container.querySelector('.config-card[data-step="' + mastDataSteps[i] + '"].is-selected');
      el.classList.remove("is-dimmed", "is-next", "is-collapsed");
      if (!hasSelection && firstIncomplete === -1) firstIncomplete = i;
    });

    mastSteps.forEach(function (id, i) {
      var el = document.getElementById(id);
      if (!el) return;
      var hasSelection = container.querySelector('.config-card[data-step="' + mastDataSteps[i] + '"].is-selected');
      if (hasSelection && !el.classList.contains("is-editing") && firstIncomplete > i) {
        el.classList.add("is-collapsed");
      }
      if (firstIncomplete >= 0 && i > firstIncomplete) {
        el.classList.add("is-dimmed");
      } else if (i === firstIncomplete && i > 0) {
        el.classList.add("is-dimmed", "is-next");
      }
    });

    /* Step dots: done state */
    var allStepIds = ["step-curve", "step-diameter", "step-length", "step-range", "step-extension", "step-baseplate"];
    var stepDataMap = {
      "step-length": "length",
      "step-diameter": "diameter",
      "step-curve": "curve",
      "step-range": "range",
      "step-extension": "extension",
      "step-baseplate": "baseplate",
    };

    allStepIds.forEach(function (id) {
      var dataStep = stepDataMap[id];
      var dot = document.querySelector('.mc-step-dots__dot[data-target="' + id + '"]');
      if (!dot) return;
      var card = container.querySelector('.config-card[data-step="' + dataStep + '"].is-selected');
      var hasSelection = !!card;
      if (hasSelection && (dataStep === "extension" || dataStep === "baseplate")) {
        hasSelection = card.dataset.value && card.dataset.value !== "none";
      }
      dot.classList.toggle("is-done", hasSelection);
    });
  }

  /* ---------------------------------------------------------------
     CARD SELECTION + AJAX PRODUCT SWITCH (no full page reload)
     Pattern: universal-variant-picker.js / fin-variant-picker-v4.js
     --------------------------------------------------------------- */
  var sectionMap = {
    length: "step-length",
    diameter: "step-diameter",
    range: "step-range",
    curve: "step-curve",
    extension: "step-extension",
    baseplate: "step-baseplate",
  };
  var productHtmlCache = {};
  var switchSeq = 0; // monotonic; ++ on every accepted switch (latest-wins token)
  var activeController = null; // AbortController for the latest in-flight fetch only
  var mediaTimeoutId = null; // tracked id for the gallery cross-fade

  /* Set a config-card's selected state + mirror aria-pressed (cards have role="button"). */
  function setSelected(card, on) {
    card.classList.toggle("is-selected", on);
    card.setAttribute("aria-pressed", on ? "true" : "false");
  }

  /* Optimistically select one card within a step group (deselect its siblings). */
  function selectCardInStep(step, card) {
    container.querySelectorAll('.config-card[data-step="' + step + '"]').forEach(function (c) {
      setSelected(c, c === card);
    });
  }

  /* Announce the resolved product into the polite live region after a switch. */
  function announce() {
    var statusEl = container.querySelector("[data-mc-status]");
    if (!statusEl) return;
    var titleEl = document.querySelector(".product-info__title");
    var priceEl = document.querySelector(".product-info__price");
    var title = titleEl ? titleEl.textContent.trim().replace(/\s+/g, " ") : "";
    var price = priceEl ? priceEl.textContent.trim().replace(/\s+/g, " ") : "";
    var msg = title ? title + " selected" : "Configuration updated";
    if (price) msg += " — " + price;
    statusEl.textContent = msg;
  }

  function syncSelections(selection) {
    if (!selection) return;
    var steps = {
      curve: selection.curve,
      diameter: selection.diameter,
      length: String(selection.length),
      range: selection.range,
    };
    Object.keys(steps).forEach(function (step) {
      var val = steps[step];
      if (!val) return;
      container.querySelectorAll('.config-card[data-step="' + step + '"]').forEach(function (card) {
        setSelected(card, card.dataset.value === val);
      });
    });
  }

  function updateProductInfoFromDoc(doc) {
    var pairs = [
      [".product-info__title", ".product-info__title"],
      [".product-info__price", ".product-info__price"],
      [".product-info__sku", ".product-info__sku"],
      [".product-info__inventory", ".product-info__inventory"],
    ];
    pairs.forEach(function (pair) {
      var next = doc.querySelector(pair[0]);
      var current = document.querySelector(pair[1]);
      if (next && current) {
        current.innerHTML = next.innerHTML;
      } else if (current && !next) {
        console.warn("[MastConfigurator] Missing element in fetched doc:", pair[0]);
      } else if (!current && next) {
        console.warn("[MastConfigurator] Missing element on current page:", pair[1]);
      }
    });
  }

  function syncConfigFromDoc(doc) {
    var newDataEl = doc.getElementById("mcData");
    if (!newDataEl) return;
    try {
      var newConfig = JSON.parse(newDataEl.textContent);
      if (newConfig.currentRange) currentRange = newConfig.currentRange;
      if (newConfig.currentDiameter) currentDiameter = newConfig.currentDiameter;
      if (newConfig.currentCurve) currentCurve = newConfig.currentCurve;
      if (newConfig.currentLength) currentLength = String(newConfig.currentLength);
      if (newConfig.currentVariantId) currentVariantId = newConfig.currentVariantId;
      syncSelections({
        range: currentRange,
        diameter: currentDiameter,
        curve: currentCurve,
        length: currentLength,
      });
    } catch (err) {
      console.warn("[MastConfigurator] Could not parse mcData from fetched doc", err);
    }
  }

  function updateProductMediaFromDoc(doc, mySeq) {
    var newMedia = doc.querySelector(".product-gallery");
    var currentMedia = document.querySelector(".product-gallery");
    if (!newMedia || !currentMedia) return;

    /* Cancel any cross-fade still pending from a previous switch. */
    if (mediaTimeoutId !== null) {
      clearTimeout(mediaTimeoutId);
      mediaTimeoutId = null;
    }

    currentMedia.style.opacity = "0";
    currentMedia.style.transition = "opacity 0.2s ease";

    var capturedHtml = newMedia.innerHTML; // snapshot now; doc may be GC'd before the timer fires
    mediaTimeoutId = setTimeout(function () {
      mediaTimeoutId = null;
      if (mySeq !== switchSeq) return; // an even-newer switch started during the 200ms window
      currentMedia.innerHTML = capturedHtml;
      if (window.customElements && typeof customElements.upgrade === "function") {
        customElements.upgrade(currentMedia);
      }
      currentMedia.style.opacity = "1";
    }, 200);
  }

  function resolveVariantIdFromDoc(doc, handle) {
    var newVariantInput = doc.querySelector("#mcDynamicVariantInput");
    if (newVariantInput && newVariantInput.value) {
      return Promise.resolve(parseInt(newVariantInput.value, 10));
    }
    return fetch("/products/" + handle + ".js")
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (product) {
        return product && product.variants && product.variants[0] ? product.variants[0].id : null;
      })
      .catch(function () {
        return null;
      });
  }

  /*
   * Resolve the main-product section id from the DOM so we can request the
   * Section Rendering API fragment instead of the whole product page. The
   * configurator lives inside that section, so closest() gives us its id.
   * Mirrors fin-variant-picker-v4.js getSectionId().
   */
  function getMastSectionId() {
    var section = container && container.closest('[id^="shopify-section-"]');
    return section ? section.id.replace("shopify-section-", "") : null;
  }

  /*
   * Fetch the product page HTML, retrying transient failures (5xx / network)
   * before giving up. Without this, a single intermittent 503 on the storefront
   * (Shopify rate-limits burst JSON/HTML requests) drops switchToMastHandle into
   * its navigation fallback and the page does a full reload -- the exact symptom
   * we are fixing. AbortError (deliberate supersession) is never retried.
   */
  function fetchMastHtmlWithRetry(url, signal, attempts) {
    attempts = attempts || 3;
    return fetch(url, { headers: { Accept: "text/html" }, signal: signal })
      .then(function (response) {
        if (!response.ok) throw new Error("Product fetch failed: " + response.status);
        return response.text();
      })
      .catch(function (err) {
        if (err && err.name === "AbortError") throw err; // superseded: stop
        if (attempts <= 1) throw err; // exhausted: let caller navigate
        return new Promise(function (resolve) {
          setTimeout(resolve, 350);
        }).then(function () {
          if (signal && signal.aborted) throw new DOMException("Aborted", "AbortError");
          return fetchMastHtmlWithRetry(url, signal, attempts - 1);
        });
      });
  }

  function switchToMastHandle(handle, selection) {
    if (!handle) return Promise.resolve(false); // [G0]
    if (handle === currentHandle) {
      // [G1] same product: no fetch
      syncSelections(selection);
      updateSelectedMast();
      updateSummary();
      announce();
      return Promise.resolve(true);
    }

    /* [G2] Accept this switch: claim a sequence number (latest-wins). */
    var mySeq = ++switchSeq;
    container.classList.add("is-switching");

    /* Supersede any in-flight fetch + pending cross-fade. */
    if (activeController) activeController.abort();
    if (mediaTimeoutId !== null) {
      clearTimeout(mediaTimeoutId);
      mediaTimeoutId = null;
    }
    var myController = new AbortController(); // closure-local; never read the shared var after this
    activeController = myController;

    var productUrl = "/products/" + handle;
    /*
     * Fetch only the main-product section fragment (~23KB transfer) instead of
     * the full page (~100KB), mirroring the fins picker. Lighter requests mean
     * less storefront rate-limiting (the 503s that triggered the reload). The
     * clean productUrl is still used for pushState and the navigation fallback.
     */
    var sectionId = getMastSectionId();
    var fetchUrl = sectionId ? productUrl + "?section_id=" + encodeURIComponent(sectionId) : productUrl;
    var fetchPromise;

    if (productHtmlCache[handle]) {
      fetchPromise = Promise.resolve(productHtmlCache[handle]);
    } else {
      fetchPromise = fetchMastHtmlWithRetry(fetchUrl, myController.signal, 3)
        .catch(function (err) {
          if (err && err.name === "AbortError") throw err;
          if (!sectionId) throw err; // already full-page; nothing left to try
          /* Section fragment failed -> recover in place via the full page before any
             reload. The full product URL is the proven cross-product baseline. */
          return fetchMastHtmlWithRetry(productUrl, myController.signal, 2);
        })
        .then(function (html) {
          productHtmlCache[handle] = html;
          return html;
        });
    }

    return fetchPromise
      .then(function (html) {
        if (mySeq !== switchSeq) return Promise.resolve(); // [G3] superseded while fetching

        var doc;
        try {
          doc = new DOMParser().parseFromString(html, "text/html");
          updateProductInfoFromDoc(doc);
          updateProductMediaFromDoc(doc, mySeq);
        } catch (domErr) {
          console.error("[MastConfigurator] DOM update failed (no navigation fallback)", domErr);
          return Promise.resolve();
        }

        return resolveVariantIdFromDoc(doc, handle)
          .then(function (variantId) {
            if (mySeq !== switchSeq) return; // [G4] superseded during variant sub-fetch
            if (variantId) {
              currentVariantId = variantId;
              var dynamicInput = document.getElementById("mcDynamicVariantInput");
              if (dynamicInput) dynamicInput.value = currentVariantId;
            }

            currentHandle = handle;
            if (selection) {
              if (selection.range) currentRange = selection.range;
              if (selection.diameter) currentDiameter = selection.diameter;
              if (selection.curve) currentCurve = selection.curve;
              if (selection.length) currentLength = String(selection.length);
              syncSelections(selection);
            } else {
              syncConfigFromDoc(doc);
            }

            /*
             * The section fragment has no <head>/<title>, so fall back to the
             * product title text to keep the browser tab in sync on switch.
             */
            var newTitle = doc.querySelector("title");
            if (newTitle && newTitle.textContent) {
              document.title = newTitle.textContent;
            } else {
              var titleEl = doc.querySelector(".product-info__title");
              if (titleEl && titleEl.textContent) document.title = titleEl.textContent.trim();
            }

            if (window.history && window.history.pushState) {
              window.history.pushState({ mastHandle: handle }, "", productUrl);
            }

            if (!selection || selection.range || selection.diameter) {
              updateAccessories();
            }
            updateRangePrices();
            updateOptionAvailability();
            updateSummary();
            announce();

            document.documentElement.dispatchEvent(
              new CustomEvent("product:changed", {
                bubbles: true,
                detail: { handle: handle, url: productUrl },
              }),
            );
          })
          .catch(function (domErr) {
            console.error("[MastConfigurator] Post-fetch update failed (no navigation fallback)", domErr);
          });
      })
      .catch(function (err) {
        if (err && err.name === "AbortError") return; // expected supersession
        if (mySeq !== switchSeq) return; // [G5] superseded failure: do NOT navigate
        console.error("[MastConfigurator] Product fetch failed, falling back to navigation", err);
        window.location.href = productUrl;
      })
      .finally(function () {
        if (mySeq === switchSeq) {
          // [G6] only the latest switch owns teardown
          container.classList.remove("is-switching");
          activeController = null;
        }
      });
  }

  function resolveMastConfigTarget(step, value) {
    var selRange = step === "range" ? value : getSelectionOrDefault("range", currentRange);
    var selDia = step === "diameter" ? value : getSelectionOrDefault("diameter", currentDiameter);
    var selCurve = step === "curve" ? value : getSelectionOrDefault("curve", currentCurve);
    var selLength = step === "length" ? value : getSelectionOrDefault("length", currentLength);
    var mast = resolveMastSelection(selRange, selDia, selCurve, selLength);

    return {
      range: selRange,
      diameter: selDia,
      curve: selCurve,
      length: selLength,
      mast: mast,
    };
  }

  container.querySelectorAll(".config-card[data-step]").forEach(function (card) {
    card.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.click();
      }
    });
    card.addEventListener("click", function () {
      var step = this.dataset.step;
      var value = this.dataset.value;

      if (this.classList.contains("is-disabled")) return;

      if (step === "curve" || step === "diameter" || step === "length" || step === "range") {
        var clicked = this;
        var target = resolveMastConfigTarget(step, value);

        if (target.mast && target.mast.handle) {
          selectCardInStep(step, clicked); // optimistic; reconciled by syncSelections on resolve
          switchToMastHandle(target.mast.handle, target);
          return;
        }

        /* Cross-length diameter: e.g. on RDM 340, click SDM (no SDM 340) ->
           jump to the nearest available SDM length instead of dead-ending. */
        if (step === "diameter") {
          var near = nearestMastForDiameter(target.range, value, target.curve, target.length);
          if (near && near.handle) {
            selectCardInStep("diameter", clicked);
            switchToMastHandle(near.handle, near);
            return;
          }
        }

        /* Cross-diameter length: e.g. SDM selected + 340 cm (RDM only) */
        if (step === "length") {
          var allowedDiameters = (clicked.dataset.diameters || "").split(",").filter(Boolean);
          if (allowedDiameters.length && allowedDiameters.indexOf(target.diameter) === -1) {
            var curveOrder = [target.curve, "constant", "fh", "fl"];
            var rangeOrder = [target.range, "hd", "essentials", "elite"];
            for (var ci = 0; ci < curveOrder.length; ci++) {
              for (var di = 0; di < allowedDiameters.length; di++) {
                for (var ri = 0; ri < rangeOrder.length; ri++) {
                  var fallback = findMastProduct(rangeOrder[ri], allowedDiameters[di], curveOrder[ci], value);
                  if (fallback && fallback.handle) {
                    var crossTarget = {
                      range: rangeOrder[ri],
                      diameter: allowedDiameters[di],
                      curve: curveOrder[ci],
                      length: value,
                    };
                    selectCardInStep("length", clicked); // light up the clicked length immediately
                    switchToMastHandle(fallback.handle, crossTarget);
                    return;
                  }
                }
              }
            }
          }
        }
        return;
      }

      /* Accessory radio: deselect siblings */
      container.querySelectorAll('.config-card[data-step="' + step + '"]').forEach(function (c) {
        c.classList.remove("is-selected");
      });
      this.classList.add("is-selected");
      updateSummary();
    });
  });

  window.addEventListener("popstate", function () {
    var path = window.location.pathname;
    var parts = path.split("/products/");
    if (parts.length < 2) return;
    var handle = parts[1].split("?")[0].split("#")[0];
    if (handle) switchToMastHandle(handle, null);
  });

  /* ---------------------------------------------------------------
     EDIT BUTTONS (expand collapsed section)
     --------------------------------------------------------------- */
  container.querySelectorAll(".config-section__edit").forEach(function (btn) {
    btn.addEventListener("click", function (e) {
      e.stopPropagation();
      var stepName = this.dataset.editStep;
      var sectionId = stepName ? "step-" + stepName : this.dataset.expand;
      var section = document.getElementById(sectionId);
      if (section) {
        section.classList.remove("is-collapsed", "is-dimmed");
        section.classList.add("is-editing");
        section.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  });

  /* ---------------------------------------------------------------
     MODALS
     --------------------------------------------------------------- */
  var mcModalStates = {};
  var mcModalClosers = {};

  // Option B embeds the complete selector. Product configuration stays on the PDP.
  var selectorFrame = document.getElementById("mcSelectorFrame");
  var selectorStatus = document.getElementById("mcSelectorStatus");
  var selectorRetry = document.getElementById("mcSelectorRetry");
  var selectorTimeout = null;
  var selectorReady = false;
  var selectorFrameUrl = "";
  var selectorRequest = null;
  var selectorDocumentUrl = "";

  function embeddedSelectorError() {
    if (selectorReady) return;
    clearTimeout(selectorTimeout);
    selectorTimeout = null;
    if (selectorRequest) selectorRequest.abort();
    selectorStatus.hidden = false;
    selectorStatus.textContent = selectorStatus.dataset.error;
    selectorRetry.hidden = false;
    selectorFrame.hidden = true;
  }

  function openEmbeddedSelector(force) {
    if (!selectorFrame) return;
    var url = new URL(config.mastSelectorUrl, window.location.origin);
    if (url.origin !== window.location.origin) return;
    url.searchParams.set("view", "embed");
    url.searchParams.set("size", getSelectionOrDefault("length", currentLength));
    url.searchParams.set("type", getSelectionOrDefault("diameter", currentDiameter).toLowerCase());
    url.searchParams.set("source", "product_page");
    if (!force && selectorFrameUrl === url.href && selectorReady) return;
    if (!force && selectorFrameUrl === url.href && selectorTimeout) return;
    selectorFrameUrl = url.href;
    selectorReady = false;
    selectorStatus.hidden = false;
    selectorStatus.textContent = selectorStatus.dataset.loading;
    selectorRetry.hidden = true;
    selectorFrame.hidden = true;
    if (selectorRequest) selectorRequest.abort();
    selectorRequest = new AbortController();
    var request = selectorRequest;
    clearTimeout(selectorTimeout);
    selectorTimeout = setTimeout(embeddedSelectorError, 15000);
    // Shopify denies direct framing. Render our own fetched embed layout as a
    // same-origin HTML document, without changing storefront security headers.
    fetch(url.href, { credentials: "same-origin", signal: request.signal })
      .then(function (response) {
        if (!response.ok || new URL(response.url).origin !== window.location.origin)
          throw new Error("Embed unavailable");
        return response.text();
      })
      .then(function (html) {
        if (request !== selectorRequest || request.signal.aborted) return;
        if (!html.includes('id="MastSelectorEmbedMain"')) throw new Error("Missing embed layout");
        var embedDocument = new DOMParser().parseFromString(html, "text/html");
        embedDocument.querySelector("base").href = url.href;
        selectorFrame.dataset.selectorUrl = url.href;
        if (selectorDocumentUrl) URL.revokeObjectURL(selectorDocumentUrl);
        selectorDocumentUrl = URL.createObjectURL(
          new Blob(["<!doctype html>" + embedDocument.documentElement.outerHTML], { type: "text/html" }),
        );
        selectorFrame.src = selectorDocumentUrl;
      })
      .catch(function (error) {
        if (error.name !== "AbortError" && request === selectorRequest) embeddedSelectorError();
      });
  }

  if (selectorFrame) {
    selectorRetry.addEventListener("click", function () {
      openEmbeddedSelector(true);
    });
    selectorFrame.addEventListener("error", embeddedSelectorError);
    window.addEventListener("message", function (event) {
      if (event.origin !== window.location.origin || event.source !== selectorFrame.contentWindow) return;
      var message = event.data;
      if (!message || typeof message.type !== "string") return;
      if (message.url !== selectorFrameUrl) return;
      var modal = document.getElementById("mcChartModal");
      var isOpen = modal.classList.contains("is-open");
      if (message.type === "unifiber:mast-selector:ready" || message.type === "unifiber:mast-selector:resize") {
        if (Number.isFinite(message.height) && message.height > 0 && message.height <= 20000) {
          selectorFrame.style.height = Math.max(250, Math.ceil(message.height)) + "px";
        }
        if (message.type === "unifiber:mast-selector:ready") {
          selectorReady = true;
          clearTimeout(selectorTimeout);
          selectorTimeout = null;
          selectorStatus.hidden = true;
          selectorRetry.hidden = true;
          selectorFrame.hidden = false;
        }
      } else if (message.type === "unifiber:mast-selector:escape" && isOpen) {
        mcModalClosers.mcChartModal();
      } else if (message.type === "unifiber:mast-selector:notify-open" && isOpen) {
        modal.classList.add("is-notify-open");
        document.getElementById("mcSelectorFullLink").inert = true;
      } else if (message.type === "unifiber:mast-selector:notify-close") {
        modal.classList.remove("is-notify-open");
        document.getElementById("mcSelectorFullLink").inert = false;
      } else if (message.type === "unifiber:mast-selector:focus-out" && isOpen) {
        var target = message.backwards
          ? modal.querySelector('[data-modal-close="mcChartModal"]')
          : document.getElementById("mcSelectorFullLink");
        target.focus();
      } else if (message.type === "unifiber:mast-selector:used") {
        try {
          sessionStorage.setItem("unifiber-mast-selector-used", "1");
        } catch (_error) {
          /* Storage is optional. */
        }
        document.dispatchEvent(new CustomEvent("mast-selector:used"));
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(
          Object.assign({}, message.detail, {
            event: "mast_selector_results_viewed",
            source: "product_page",
            method: "embed",
          }),
        );
        if (isOpen && Number.isFinite(message.resultsTop)) {
          requestAnimationFrame(function () {
            modal.scrollTop +=
              selectorFrame.getBoundingClientRect().top - modal.getBoundingClientRect().top + message.resultsTop;
          });
        }
      } else if (
        message.type === "unifiber:mast-selector:cart" &&
        ["variant:add", "cart:change"].includes(message.event)
      ) {
        if (!message.detail || !message.detail.cart) return;
        if (isOpen) mcModalClosers.mcChartModal();
        document.documentElement.dispatchEvent(
          new CustomEvent(message.event, { bubbles: true, detail: message.detail }),
        );
      }
    });
  }

  function setupModal(modalId) {
    var modal = document.getElementById(modalId);
    var trigger = document.querySelector('[data-modal-trigger="' + modalId + '"]');
    var overlay = document.querySelector('[data-modal-overlay="' + modalId + '"]');
    var closeBtn = document.querySelector('[data-modal-close="' + modalId + '"]');
    if (!trigger || !modal) return;

    var roots = [modal];
    if (overlay) roots.push(overlay);
    if (closeBtn) roots.push(closeBtn);

    function open() {
      modal.classList.add("is-open");
      if (overlay) overlay.classList.add("is-open");
      if (closeBtn) closeBtn.classList.add("is-visible");
      document.body.classList.add("mc-modal-open");
      trigger.setAttribute("aria-expanded", "true");
      if (modalId === "mcChartModal") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "mast_selector_open", source: "product_page", method: "embed" });
        openEmbeddedSelector(false);
      }

      var a11y = window.UnifiberModalA11y;
      if (a11y) {
        mcModalStates[modalId] = a11y.activate({
          roots: roots,
          trigger: trigger,
          focusTarget: closeBtn,
        });
      } else {
        modal.setAttribute("aria-hidden", "false");
        if (closeBtn) closeBtn.focus();
      }
    }

    function close() {
      if (modalId === "mcChartModal" && modal.classList.contains("is-notify-open")) {
        selectorFrame.contentWindow.closeNotifyModal();
        modal.classList.remove("is-notify-open");
        document.getElementById("mcSelectorFullLink").inert = false;
      }
      modal.classList.remove("is-open");
      modal.setAttribute("aria-hidden", "true");
      if (overlay) overlay.classList.remove("is-open");
      if (closeBtn) closeBtn.classList.remove("is-visible");
      document.body.classList.remove("mc-modal-open");
      trigger.setAttribute("aria-expanded", "false");

      var a11y = window.UnifiberModalA11y;
      if (a11y && mcModalStates[modalId]) {
        a11y.deactivate(mcModalStates[modalId]);
        delete mcModalStates[modalId];
      } else if (trigger.focus) {
        trigger.focus();
      }
    }

    mcModalClosers[modalId] = close;

    trigger.addEventListener("click", open);
    trigger.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        open();
      }
    });
    if (overlay) overlay.addEventListener("click", close);
    if (closeBtn) closeBtn.addEventListener("click", close);
  }

  setupModal("mcKnowledgeModal");
  setupModal("mcSpecsModal");
  setupModal("mcChartModal");

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") {
      Object.keys(mcModalClosers).forEach(function (modalId) {
        var modal = document.getElementById(modalId);
        if (modal && modal.classList.contains("is-open")) {
          mcModalClosers[modalId]();
        }
      });
    }
  });

  /* ---------------------------------------------------------------
     STICKY ATC BAR
     --------------------------------------------------------------- */
  var buySection = document.getElementById("mcConfigBuy");
  var stickyBar = document.getElementById("mcStickyAtc");

  if (buySection && stickyBar) {
    var stickyFrame = null;
    var stickyButton = stickyBar.querySelector("button");

    function updateStickyVisibility() {
      var shouldShow = buySection.getBoundingClientRect().bottom <= 0;
      stickyBar.classList.toggle("is-visible", shouldShow);
      stickyBar.setAttribute("aria-hidden", shouldShow ? "false" : "true");
      if (stickyButton) stickyButton.setAttribute("tabindex", shouldShow ? "0" : "-1");
      stickyFrame = null;
    }

    function scheduleStickyVisibility() {
      if (stickyFrame === null) {
        stickyFrame = window.requestAnimationFrame(updateStickyVisibility);
      }
    }

    /* Start hidden and measure the actual viewport position. This avoids an
       IntersectionObserver entry leaving the bar visible while the buy block
       is still below the fold on tablet layouts. */
    stickyBar.classList.remove("is-visible");
    stickyBar.setAttribute("aria-hidden", "true");

    var stickyObserver = new IntersectionObserver(updateStickyVisibility, { threshold: 0 });
    stickyObserver.observe(buySection);
    window.addEventListener("scroll", scheduleStickyVisibility, { passive: true });
    window.addEventListener("resize", scheduleStickyVisibility);
    updateStickyVisibility();
  }

  /* ---------------------------------------------------------------
     STEP DOTS: click to scroll
     --------------------------------------------------------------- */
  document.querySelectorAll(".mc-step-dots__dot").forEach(function (dot) {
    dot.addEventListener("click", function () {
      var target = document.getElementById(this.dataset.target);
      if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  });

  /* Step dots: scroll-spy */
  var allStepIds = ["step-curve", "step-diameter", "step-length", "step-range", "step-extension", "step-baseplate"];

  var dotObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          document.querySelectorAll(".mc-step-dots__dot").forEach(function (d) {
            d.classList.remove("is-active");
          });
          var activeDot = document.querySelector('.mc-step-dots__dot[data-target="' + entry.target.id + '"]');
          if (activeDot) activeDot.classList.add("is-active");
        }
      });
    },
    { rootMargin: "-40% 0px -40% 0px", threshold: 0 },
  );

  allStepIds.forEach(function (id) {
    var el = document.getElementById(id);
    if (el) dotObserver.observe(el);
  });

  /* ---------------------------------------------------------------
     ADD TO CART
     --------------------------------------------------------------- */
  function addToCart(button) {
    if (button.disabled || button.getAttribute("aria-busy") === "true") return;

    button.setAttribute("aria-busy", "true");
    var originalText = button.textContent;
    button.textContent = "Adding...";

    if (!currentVariantId) {
      button.removeAttribute("aria-busy");
      button.textContent = originalText;
      document.documentElement.dispatchEvent(
        new CustomEvent("theme:toast:show", {
          bubbles: true,
          detail: {
            message: "This configuration is currently out of stock",
            tone: "error",
          },
        }),
      );
      return;
    }

    var items = [{ id: currentVariantId, quantity: 1 }];

    /* Extension */
    var extCard = container.querySelector('.config-card[data-step="extension"].is-selected');
    if (extCard && extCard.dataset.value !== "none" && extCard.dataset.variantId) {
      items.push({ id: parseInt(extCard.dataset.variantId, 10), quantity: 1 });
    }

    /* Baseplate */
    var bpCard = container.querySelector('.config-card[data-step="baseplate"].is-selected');
    if (bpCard && bpCard.dataset.value !== "none" && bpCard.dataset.variantId) {
      items.push({ id: parseInt(bpCard.dataset.variantId, 10), quantity: 1 });
    }

    /* Collect bundled sections for cart drawer update (Impact theme pattern).
       Seed with 'variant-added' exactly like the native product-form (_onSubmit),
       so the cart/add response carries the section the drawer needs. The theme's
       CartDrawer._onPrepareBundledSections also pushes its own section id, so this
       MUST be an array (Set.push is undefined). The drawer then opens by itself on
       the 'variant:add' event (CartDrawer._onVariantAdded -> show()). */
    var sectionsToBundle = ["variant-added"];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      }),
    );

    var bodyPayload = { items: items };
    if (sectionsToBundle.length > 0) {
      bodyPayload.sections = sectionsToBundle.join(",");
    }

    var routeRoot = window.Shopify && Shopify.routes ? Shopify.routes.root : "/";

    /* Fire cart/add.js and cart.js in parallel — no waiting for sequential chain */
    var addPromise = fetch(routeRoot + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(bodyPayload),
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (d) {
          throw new Error(d.description || d.message || "Could not add to cart");
        });
      }
      return r.json();
    });

    addPromise
      .then(function (data) {
        /* Reset button immediately on add success — instant feedback */
        button.removeAttribute("aria-busy");
        button.textContent = originalText;
        /* Fetch full cart and dispatch events (drawer opens) */
        return fetch(routeRoot + "cart.js")
          .then(function (r) {
            return r.json();
          })
          .then(function (cart) {
            if (data.sections) cart.sections = data.sections;
            document.documentElement.dispatchEvent(
              new CustomEvent("variant:add", {
                bubbles: true,
                detail: { items: data.items, cart: cart },
              }),
            );
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:change", {
                bubbles: true,
                detail: { baseEvent: "variant:add", cart: cart },
              }),
            );
          });
      })
      .catch(function (err) {
        console.error("[MastConfigurator] ATC error:", err);
        button.removeAttribute("aria-busy");
        button.textContent = originalText;
        document.documentElement.dispatchEvent(
          new CustomEvent("theme:toast:show", {
            bubbles: true,
            detail: { message: err.message || "Could not add to cart", tone: "error" },
          }),
        );
      });
  }

  var atcButton = document.getElementById("mcAtcButton");
  var stickyAtcButton = document.getElementById("mcStickyAtcBtn");

  if (atcButton)
    atcButton.addEventListener("click", function () {
      addToCart(this);
    });
  if (stickyAtcButton)
    stickyAtcButton.addEventListener("click", function () {
      addToCart(this);
    });

  /* ---------------------------------------------------------------
     INIT
     --------------------------------------------------------------- */

  function initCollapsedSteps() {
    ["curve", "diameter", "length"].forEach(function (step) {
      var section = document.getElementById("step-" + step);
      var selected = container.querySelector('.config-card[data-step="' + step + '"].is-selected');
      if (section && selected) {
        section.classList.add("is-collapsed");
      }
    });
  }

  updateRangePrices();
  updateAccessories();
  updateOptionAvailability();
  initCollapsedSteps();
  updateSummary();

  /* Mirror initial aria-pressed on server-rendered option cards. */
  ["curve", "diameter", "length", "range"].forEach(function (step) {
    container.querySelectorAll('.config-card[data-step="' + step + '"]').forEach(function (card) {
      card.setAttribute("aria-pressed", card.classList.contains("is-selected") ? "true" : "false");
    });
  });

  /* Carbon bar reveal animation: add is-filled class to trigger CSS width transition */
  function animateCarbonBars() {
    document.querySelectorAll(".range-card__bar").forEach(function (bar) {
      bar.classList.add("is-filled");
    });
  }
  var rangeStep = document.getElementById("step-range");
  if (rangeStep && "IntersectionObserver" in window) {
    var carbonObs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            requestAnimationFrame(animateCarbonBars);
            carbonObs.disconnect();
          }
        });
      },
      { threshold: 0 },
    );
    carbonObs.observe(rangeStep);
  }
  /* Safety fallback */
  setTimeout(animateCarbonBars, 800);
})();
